import { useEffect, useRef, useState } from 'react';

type TimePropType =
  | {
      days?: number;
      hours?: number;
      minutes?: number;
      seconds?: number;
    }
  | undefined;

export type TimerProps = {
  className?: string;
  showSeconds?: boolean;
  showDays?: boolean;
  time: TimePropType;
};

export const useTimer = ({ time, showSeconds, showDays }: TimerProps) => {
  const [timer, setTimer] = useState(time);

  const timeOutRef = useRef<NodeJS.Timeout | undefined>(null);

  // Reset timer if parent changes the time value
  useEffect(() => {
    if (timeOutRef?.current) clearTimeout(timeOutRef?.current);
    setTimer(time);
  }, [time]);

  // START - Functions to calculate next timer values
  const getNextHour = () => (timer?.hours === 0 ? 23 : (timer?.hours || 0) - 1);

  const getNextMinute = () =>
    timer?.minutes === 0 ? 59 : (timer?.minutes || 0) - 1;

  const getNextTimer = () => {
    // Next timer values
    const secondsFinished = timer?.seconds === 0 || !showSeconds;
    const minutesRollover = timer?.minutes === 0 && secondsFinished;

    const nextDaysValue = timer?.days ? timer?.days - 1 : 0;
    const nextDays =
      timer?.hours === 0 && minutesRollover ? nextDaysValue : timer?.days;

    const nextHours = minutesRollover ? getNextHour() : timer?.hours;

    const nextMinutes = secondsFinished ? getNextMinute() : timer?.minutes;

    const nextSeconds = secondsFinished ? 59 : (timer?.seconds || 0) - 1;

    // return next timer values
    return {
      days: nextDays,
      hours: nextHours,
      minutes: nextMinutes,
      seconds: nextSeconds,
    };
  };

  // END - Functions to calculate next timer values

  // Update timer every second/minute
  useEffect(() => {
    if (!timer) return; // Do nothing if timer is undefined

    const isTimerFinished =
      (!showDays || timer?.days === 0) &&
      timer?.hours === 0 &&
      timer?.minutes === 0 &&
      (!showSeconds || timer?.seconds === 0);

    if (!timer || isTimerFinished) return;

    // Set delay and next timer state logic based on seconds presence
    const delay = showSeconds ? 1000 : 60000;

    timeOutRef.current = setTimeout(() => {
      setTimer(getNextTimer());
    }, delay);

    // clear timeout on re-run/unmount
    return () => {
      if (timeOutRef.current) clearTimeout(timeOutRef.current);
    };
  }, [timer]);

  return { timer };
};
